import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, VerifyEmailCardSkeleton } from '@shared/ui';
import { useCheckEmail } from '@features/auth/verify-email';
import { LocaleThemeControls } from '@features/change-language';

export function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { token = '' } = useParams<{ token: string }>();
  const { checkEmail, isLoading, errorI18nKey, errorMessage } = useCheckEmail();
  const startedRef = useRef(false);
  const [result, setResult] = useState<'idle' | 'success' | 'already_verified' | 'error'>('idle');

  useEffect(() => {
    if (!token || startedRef.current) {
      return;
    }

    startedRef.current = true;

    checkEmail(token).then((checkResult) => {
      setResult(checkResult === 'success' ? 'success' : checkResult);

      if (checkResult === 'success') {
        navigate('/balance', { replace: true });
      }
    });
  }, [token, checkEmail, navigate]);

  const isAlreadyVerified = result === 'already_verified';
  const displayError = errorI18nKey ? t(errorI18nKey) : errorMessage;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="relative w-full max-w-md rounded-xl border border-border bg-card p-6 space-y-4 text-center">
        <div className="absolute right-4 top-4 flex items-center gap-1">
          <LocaleThemeControls className="flex items-center gap-1" />
        </div>

        <h1 className="text-2xl font-bold text-foreground pt-2">{t('confirmEmail')}</h1>

        {isLoading && <VerifyEmailCardSkeleton />}

        {!isLoading && displayError && (
          <>
            <p className={isAlreadyVerified ? 'text-muted-foreground' : 'text-destructive'}>
              {displayError}
            </p>
            {isAlreadyVerified && (
              <p className="text-sm text-muted-foreground">{t('emailAlreadyVerifiedHint')}</p>
            )}
            <Button
              onClick={() => navigate('/auth', { replace: true })}
              variant={isAlreadyVerified ? 'default' : 'secondary'}
              className="w-full"
            >
              {t('backToSignIn')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
